import React from 'react';
import DOMPurify from 'dompurify';
import { BlockMath, InlineMath } from 'react-katex';
import ReactMarkdown from 'react-markdown';
import { dark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeExternalLinks from 'rehype-external-links';
import 'katex/dist/katex.min.css';
import DCopyBtn from '@/components/Global/DCopyBtn';
// import './style.css';

const SyntaxHighlighter = React.lazy(() =>
  import('react-syntax-highlighter/dist/esm/prism')
);

const Pre = ({ children }) => (
  <pre className="blog-pre">
    <DCopyBtn>{children}</DCopyBtn>
    {children}
  </pre>
);

const OrderList = ({ children, start }) => (
  <ol className="ml-[15px] list-outside list-decimal space-y-0 !mt-1" start={start}>
    {children}
  </ol>
);
const UnorderedList = ({ children }) => (
  <ul className="ml-[20px] list-outside list-disc space-y-0 !mt-1">{children}</ul>
);
const ListItem = ({ children }) => <li className="my-0">{children}</li>;
const Paragraph = ({ children }) => (
  <p className="peer peer:mt-size3 leading-6" style={{ fontSize: 'var(--dt-font-size)' }}>
    {children}
  </p>
);
const Link = ({ children, href, target, ...props }) => (
  <a
    className=""
    style={{ color: 'var(--dt-color-brand-100)' }}
    href={href}
    target={target}
    {...props}
  >
    {children}
  </a>
);

const HorizontalRule = () => (
  <hr className="my-4 border-t border-gray-300" />
);

const ImageRenderer = ({ src, alt }) => {
  // Start with the original src
  let cleanSrc = src;
  cleanSrc = cleanSrc.replace('https:/<b>', 'https://');
  cleanSrc = cleanSrc.replace(/<b>|<\/b>/g, '/');
  return <img src={cleanSrc} alt={alt} className="custom-markdown-image" />;
};



const sanitizeContent = (content, isUser) => {
  if (!content) return '';

  // First, preserve horizontal rules by replacing them with a special marker
  let fixedContent = content
    .replace(/^\s*-{3,}\s*$/gm, '___HORIZONTAL_RULE___')
    .trim();

  // Split by newlines, preserve empty lines for spacing
  const lines = fixedContent.split('\n');

  // Process lines to maintain proper spacing
  const processedLines = [];
  let previousLineEmpty = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Handle horizontal rule markers
    if (trimmedLine === '___HORIZONTAL_RULE___') {
      processedLines.push('---');
      continue;
    }

    // If this is an empty line
    if (trimmedLine.length === 0) {
      // Only add one empty line (avoid multiple consecutive empty lines)
      if (!previousLineEmpty) {
        processedLines.push('');
        previousLineEmpty = true;
      }
    } else {
      // This is a non-empty line
      processedLines.push(line);
      previousLineEmpty = false;
    }
  }

  // Join the processed lines back together
  fixedContent = processedLines.join('\n');

  // Use DOMPurify only for user-generated content
  if (isUser) {
    fixedContent = DOMPurify.sanitize(fixedContent);
  }

  // Escape isolated dollar signs that aren't part of a math expression
  fixedContent = fixedContent.replace(/([^\\])\$(?!\d)/g, '$1\\$');
  return fixedContent;
};

const generateMarkdownBubble = (urlTransform, ele) => {
  const isUser = ele?.role === 'user';
  const content = sanitizeContent(ele?.content, isUser);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[
        rehypeRaw,
        rehypeKatex,
        [rehypeExternalLinks, { target: '_blank', rel: 'noreferrer' }]
      ]}
      urlTransform={urlTransform}
      components={{
        p: Paragraph,
        pre: Pre,
        a: Link,
        ul: UnorderedList,
        ol: ({ start, children, ...props }) => <OrderList start={start} {...props}>{children}</OrderList>,
        li: ListItem,
        img: ImageRenderer,
        hr: HorizontalRule,
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          return !inline && match ? (
            <SyntaxHighlighter
              {...props}
              style={dark}
              language={match[1]}
              PreTag="div"
              className="custom-syntax-highlighter"
            >
              {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
          ) : (
            <code {...props} className={className}>
              {children}
            </code>
          );
        },
        math: ({ value }) => <BlockMath>{value}</BlockMath>,
        inlineMath: ({ value }) => <InlineMath>{value}</InlineMath>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default generateMarkdownBubble;
